import clsx from "clsx";
import { FC } from "react";

import { GroupMenuButton } from "components/materials/page/interaction-mode/GroupMenuButton";
import { StudentInteractionModeView } from "components/materials/page/interaction-mode/StudentInteractionModeView";
import {
  INTERACTION_MODE_CHIP_KINDS,
  INTERACTION_MODE_ICONS,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "components/materials/page/interaction-mode/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Chip } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { ClickEvent, Menu, MenuItem } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon, PageIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";
import { ComponentEventProperties } from "utils/amplitude";
import { isGroupPage } from "utils/page-utils";

export type InteractionModeMenuProps = {
  page: PageType;
  hasSubmissions: boolean;
  onAuthorClick?: (interactionMode: InteractionMode) => void;
  onStudentGroupCardToggle?: () => void;
  readOnly?: boolean;
  pageGroupIndex: number;
  showDisabledChips?: boolean;
  showNormalGroupButton?: boolean;
  readOnlyTooltip?: string;
};

export const InteractionModeMenu: FC<InteractionModeMenuProps> = ({
  page,
  onAuthorClick,
  onStudentGroupCardToggle,
  hasSubmissions,
  readOnly = false,
  pageGroupIndex,
  showNormalGroupButton = false,
  showDisabledChips = false,
  readOnlyTooltip,
}) => {
  const { canParticipateInSection } = useCapabilities();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isAssessing = useIsAssessing();
  const isReadOnly = readOnly;
  const isDisabled = hasSubmissions || isAssessing;
  const showNormalButton = showNormalGroupButton || !isGroupPage(page);

  if (page.interaction_mode === InteractionMode.STATIC) {
    if (!showDisabledChips) {
      return null;
    }

    return (
      <div className="w-fit">
        <Chip kind="gray">
          <IconText
            iconStart={<PageIcon />}
            text={t("page_interaction_mode_menu.interaction_mode.static")}
          />
        </Chip>
      </div>
    );
  }

  if (canParticipateInSection) {
    return (
      <StudentInteractionModeView
        page={page}
        pageGroupIndex={pageGroupIndex}
        onStudentGroupCardToggle={onStudentGroupCardToggle}
      />
    );
  }

  const tooltipText =
    (isReadOnly && readOnlyTooltip) ||
    (hasSubmissions && isEditModeEnabled ? t("tooltip.group_config_has_submissions") : "");

  if (showNormalButton && (isReadOnly || isDisabled)) {
    return (
      <Tooltip text={tooltipText}>
        <Chip
          kind={showDisabledChips ? "gray" : INTERACTION_MODE_CHIP_KINDS[page.interaction_mode]}
        >
          <IconText
            containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
            containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
            iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
            shortText={
              page.interaction_mode === InteractionMode.SHARED_CLASS
                ? t("page_interaction_mode_menu.interaction_mode.all_short")
                : undefined
            }
            text={t(`page_interaction_mode_menu.interaction_mode.${page.interaction_mode}`)}
          />
        </Chip>
      </Tooltip>
    );
  }

  const onItemClick = (e: ClickEvent) => {
    const interactionMode = e.value as InteractionMode;
    onAuthorClick?.(interactionMode);
  };

  const MenuButton = showNormalButton ? (
    <Chip as="button" kind={INTERACTION_MODE_CHIP_KINDS[page.interaction_mode]}>
      <IconText
        containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
        containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
        iconEnd={<ChevronDownIcon />}
        iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
        shortText={
          page.interaction_mode === InteractionMode.SHARED_CLASS
            ? t("page_interaction_mode_menu.interaction_mode.all_short")
            : undefined
        }
        text={t(`page_interaction_mode_menu.interaction_mode.${page.interaction_mode}`)}
      />
    </Chip>
  ) : (
    <GroupMenuButton
      page={page}
      pageGroupIndex={pageGroupIndex}
      restricted={isReadOnly || isDisabled}
      onGroupConfigCardToggle={onAuthorClick}
      onStudentGroupCardToggle={onStudentGroupCardToggle}
    />
  );

  return (
    <Tooltip text={tooltipText}>
      <div>
        <Menu
          menuButton={MenuButton}
          title={t("page_interaction_mode_menu.change_page_interaction_mode")}
          onItemClick={onItemClick}
        >
          {!isReadOnly && !isDisabled && (
            <>
              <InteractionModeMenuItem
                interactionMode={InteractionMode.INDIVIDUAL}
                key="1"
                page={page}
              />
              <InteractionModeMenuItem
                interactionMode={InteractionMode.SHARED_CLASS}
                key="2"
                page={page}
              />
              <InteractionModeMenuItem
                interactionMode={
                  page.interaction_mode === InteractionMode.SELF_ASSIGN
                    ? page.interaction_mode
                    : InteractionMode.ASSIGN_STUDENTS
                }
                key="3"
                page={page}
              />
            </>
          )}
        </Menu>
      </div>
    </Tooltip>
  );
};

type InteractionModeMenuItemProps = ComponentEventProperties & {
  interactionMode: InteractionMode;
  page: PageType;
};

const InteractionModeMenuItem: FC<InteractionModeMenuItemProps> = ({ interactionMode, page }) => {
  const checked = interactionMode === page.interaction_mode;

  if (interactionMode === InteractionMode.STATIC) {
    return null;
  }

  return (
    <MenuItem
      checked={checked}
      className={clsx({ selected: checked })}
      eventName="Page type updated"
      eventProps={{
        pageInteractionMode: interactionMode,
        pageTitle: page.title,
        pageId: page.id,
      }}
      value={interactionMode}
    >
      <IconText
        iconStart={INTERACTION_MODE_ICONS[interactionMode]}
        text={t(`page_interaction_mode_menu.interaction_mode.${interactionMode}`)}
      />
    </MenuItem>
  );
};
